import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';

import TotalCustomerIcon from 'core/status-icon/TotalCustomerIcon';

import ExpiredStatusIcon from 'core/status-icon/ExpiredStatusIcon';
import RejectedStatusIcon from 'core/status-icon/RejectedStatusIcon';
import CompletedStatusIcon from 'core/status-icon/CompletedStatusIcon';
import StatusNotStartedIcon from 'core/status-icon/StatusNotStartedIcon';

import { getCustomersKycCount } from 'app/features/customers/customersKycCountSaga';

const HorizontalWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  borderRadius: 15,
  boxShadow: '10px 8px 10px 2px rgba(0, 0, 0, 0.06)',
  width: '100%',
  background: theme.palette.common.white,
  padding: '16px',
  overflowX: 'auto',
  marginBottom: '10px',
}));

const CardItem = styled(Box)(() => ({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '8px',
  minWidth: '203px',
  borderRadius: '12px',
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

const ListItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid #eee',
}));

export default function CustomerStat({ fromDate, toDate, pageName, layout = 'horizontal' }) {
  const dispatch = useDispatch();

  const { data: customerKycResponse, isLoading } = useSelector(
    (state) => state.customersKycCount.index
  );

  const customerKycData = customerKycResponse?.data;
  const total = customerKycData?.totalUsersCount ?? 0;

  useEffect(() => {
    dispatch(getCustomersKycCount());
  }, [dispatch]);

  const statsData = [
    { label: 'Total Customer', icon: <TotalCustomerIcon />, count: total },
    { label: 'Total Verified', icon: <CompletedStatusIcon />, count: customerKycData?.kycCompletedCount ?? 0 },
    { label: 'Total Rejected', icon: <RejectedStatusIcon />, count: customerKycData?.kycRejectedCount ?? 0 },
    { label: 'Total Expired', icon: <ExpiredStatusIcon />, count: customerKycData?.kycExpiredCount ?? 0 },
    { label: 'Total Not Started', icon: <StatusNotStartedIcon />, count: customerKycData?.kycNotStartedCount ?? 0 },
  ];

  if (layout === 'vertical') {
    return (
      <Box
        sx={{
          background: '#fff',
          padding: 2,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          maxWidth: "auto",
        }}
      >
        <Typography variant="h6" mb={2}>
          KYC Report
        </Typography>

        {statsData.map((stat, index) => {
        

          return (
            <ListItem key={index}>
              <Box display="flex" alignItems="center" gap={1}>
                {stat.icon}
                <Typography fontSize="0.95rem">{stat.label}</Typography>
              </Box>
              {isLoading ? (
                <Skeleton width="60px" />
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>{stat.count}</Typography>
                  <Typography
                    fontSize="0.75rem"
                    sx={{
                      background: '#f2f2f2',
                      borderRadius: '8px',
                      px: 1,
                      py: 0.2,
                      fontWeight: 500,
                    }}
                  >
                  
                  </Typography>
                </Box>
              )}
            </ListItem>
          );
        })}
      </Box>
    );
  }

  // Horizontal card layout
  return (
    <HorizontalWrapper>
      {statsData.map((stat, index) => {
        const percentage =
          total > 0 ? `${Math.round((stat.count / total) * 100)}%` : '0%';

        return (
          <CardItem key={index}>
            {stat.icon}
            <Box display="flex" flexDirection="column">
              <Typography fontSize="0.857rem" mb="0.5rem" color="text.secondary">
                {stat.label}
              </Typography>
              {isLoading ? (
                <Skeleton width="100px" />
              ) : (
                <Box display="flex" alignItems="center" gap="6px">
                  <Typography fontSize="1.429rem" fontWeight={600}>
                    {stat.count}
                  </Typography>
                  <Typography fontSize="0.857rem" color="text.secondary">
                    {percentage}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardItem>
        );
      })}
    </HorizontalWrapper>
  );
}

CustomerStat.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  pageName: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
};
